import { useEffect, useRef, useState } from 'react';

import { onError } from '../../../utils/error.util';
import { getCommercialApplication } from '../../../services/commercial-applications.service';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../../utils/content.util';
import { CommercialApplication } from '../../../models/commercial-application.model';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Col, Row } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { formatMoney } from '../../../utils/format.util';
import { Dealer } from '../../../models/dealer.model';
import { getDealer } from '../../../services/dealers.service';

export const formatSSN = (ssn?: string): string => {
  if (!ssn) return '';
  if (ssn.length === 9) {
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
  }
  return ssn;
};

export function formatUTCDate(dateString: string | undefined, formatString: string = 'MM/dd/yyyy'): string {
  if (!dateString) return '';

  try {
    const parsedDate = parseISO(dateString);
    const utcDate = toZonedTime(parsedDate, 'UTC');
    return format(utcDate, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

export default function CommercialFormController() {
  const { isAuthenticated } = useAppContext();
  const navigate = useNavigate();

  const contentToPrint = useRef<any>(null);

  const handlePrint = useReactToPrint({ contentRef: contentToPrint });

  const { applicationId } = useParams();
  const [application, setApplication] = useState<CommercialApplication | undefined>(undefined);
  const [dealer, setDealer] = useState<Dealer | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  async function onLoad() {
    if (!isAuthenticated || !applicationId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await getCommercialApplication(applicationId);
      setApplication(response);

      if (response?.dealerId) {
        const dealerResponse = await getDealer(response.dealerId);
        setDealer(dealerResponse);
      }
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderApplication() {
    return (
      <div>
        <Row className="my-3">
          <Col>
            <Button onClick={() => handlePrint()} variant="outlined" startIcon={<PrintIcon />} className="mr-2">
              Print
            </Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <div id="pdfview">
          <div className="application-form-print" id={`commercial-application-${applicationId}`} ref={contentToPrint}>
            <section className="pdfcol-full">
              <img alt="logo" id="print-logo" src="/assets/images/tsf-logo.png" />
              <p id="pdfdate">
                <small>
                  <strong>Date:</strong>{' '}
                  {application?.createdAt ? formatUTCDate(application?.createdAt.toISOString()) : ''}
                  {application?.createdBy ? (
                    <>
                      {' | '}
                      <strong>Created By:</strong> {application.createdBy}
                    </>
                  ) : null}
                  {application?.isPhoneApplication ? (
                    <>
                      {' | '}
                      <strong>Phone Application</strong>
                      {application?.phoneNumberCallback ? (
                        <>
                          {' | '}
                          <strong>Callback:</strong> {application.phoneNumberCallback}
                        </>
                      ) : null}
                    </>
                  ) : null}
                </small>
              </p>
            </section>

            <section className="pdfcol">
              <h6>Primary Contact Information</h6>
              <p>
                <strong>Contact Name:</strong>
                <br />
                {application?.firstName} {application?.lastName}
                <br />
                <strong>p:</strong> {application?.primaryPhoneNumber}
                <br />
                <strong>e:</strong> {application?.emailAddress}
              </p>
            </section>

            <section className="pdfcol">
              <h6>Business Details</h6>
              <p>
                <strong>DBA:</strong> {application?.dba}
                <br />
                <strong>Federal Tax ID:</strong> {application?.federalTaxId}
                <br />
                <strong>Years in Business:</strong> {application?.yearsInBusiness}
                <br />
                <strong>Gross Annual Revenue:</strong> {formatMoney(application?.grossAnnualRevenue)}
                <br />
                <strong>Legal Entity Type:</strong> {application?.legalEntityType}
                <br />
                <strong>Owners Own More Than 25%:</strong> {application?.ownersOwnMoreThan25}
                <br />
                <strong>What Company Does:</strong> {application?.whatCompanyDoes}
              </p>

              <p>
                <strong>Business Address:</strong>
                <br />
                {application?.businessAddressLine1}
                <br />
                {application?.businessCity}, {application?.businessState} {application?.businessZipCode}
                <br />
                <strong>Website:</strong> {application?.businessWebsite}
              </p>
            </section>

            <section className="pdfcol">
              <h6>Ownership Information</h6>
              {application?.owners && application.owners.length > 0 ? (
                application.owners.map((owner, index) => (
                  <div key={index}>
                    <p>
                      <strong>Owner {index + 1}:</strong>
                      <br />
                      {owner.ownerFirstName} {owner.ownerLastName}
                      <br />
                      <strong>Job Title:</strong> {owner.ownerJobTitle}
                      <br />
                      <strong>Phone:</strong> {owner.ownerPhoneNumber}
                      <br />
                      <strong>Ownership %:</strong> {owner.ownershipPercentage}%
                      <br />
                      <strong>SSN:</strong> {formatSSN(owner.ownerSSN)}
                      <br />
                      <strong>DOB:</strong> {formatUTCDate(owner.ownerDateOfBirth)}
                      <br />
                      <strong>Citizenship:</strong> {owner.ownerCitizenship}
                      {owner.ownerCitizenship === 'Other' && owner.ownerCitizenshipOther ? (
                        <>
                          {' - '}
                          {owner.ownerCitizenshipOther}
                        </>
                      ) : null}
                    </p>

                    <p>
                      <strong>Owner Address:</strong>
                      <br />
                      {owner.ownersAddressLine1}
                      <br />
                      {owner.ownersCity}, {owner.ownersState} {owner.ownersZipCode}
                    </p>
                    {index < application.owners.length - 1 && <hr />}
                  </div>
                ))
              ) : (
                <p>No ownership information provided</p>
              )}
            </section>

            <section className="pdfcol">
              <h6>Financing Information</h6>
              <p>
                <strong>Financing Types:</strong>
                <br />
                {application?.isEquipmentFinancing ? '• Equipment Financing' : ''}
                {application?.isWorkingCapitalFinancing ? '• Working Capital Financing' : ''}
                {application?.isLineOfCreditFinancing ? '• Line of Credit Financing' : ''}
                {application?.isSmallBusinessLoanFinancing ? '• Small Business Loan Financing' : ''}
              </p>

              <p>
                <strong>Amount Requested:</strong> {formatMoney(application?.amountRequested)}
              </p>

              {application?.isEquipmentFinancing ? (
                <p>
                  <strong>Equipment Details:</strong>
                  <br />
                  <strong>Year:</strong> {application?.equipmentYear}
                  <br />
                  <strong>Make:</strong> {application?.equipmentMake}
                  <br />
                  <strong>Model:</strong> {application?.equipmentModel}
                </p>
              ) : null}

              <p>
                <strong>Period Finalizing:</strong> {application?.periodFinalizing}
              </p>
            </section>

            <section className="pdfcol">
              <h6>Dealer Information</h6>
              {application?.dealerName ? (
                <>
                  <p>
                    <strong>Dealer Name:</strong> {application?.dealerName}
                    {dealer?.address ? (
                      <>
                        <br />
                        {dealer?.address.addressLine1}
                        <br />
                        {dealer?.address.city}, {dealer?.address.state} {dealer?.address.zipCode}
                      </>
                    ) : undefined}
                  </p>
                  <p>
                    <strong>Location:</strong> {application?.dealerLocation}
                    <br />
                    <strong>Stock #:</strong> {application?.dealerStock}
                  </p>
                </>
              ) : (
                <p>No dealer information provided</p>
              )}
            </section>

            <section className="pdfcol-full">
              {application?.customAttributes && Object.keys(application.customAttributes).length > 0 ? (
                <>
                  <h6>Custom Attributes</h6>
                  {Object.entries(application.customAttributes).map(([key, value]) => (
                    <p key={key}>
                      <strong>{key}:</strong> {value}
                    </p>
                  ))}
                </>
              ) : null}
            </section>

            <section className="pdfcol-full" id="pdfconsent">
              <p>
                BY CHECKING BELOW, I VERIFY MY INTENT TO APPLY FOR COMMERCIAL CREDIT AUTHORIZATION. I certify that the
                above information stated in this application is true and correct and a complete statement of my
                business's financial condition. I understand that this application will be kept whether or not it is
                approved. You are authorized to share this application with other potential lenders. You and any
                potential or subsequent creditor are authorized to check my credit and my business history to answer
                questions about your credit experience with me and to disclose credit information to each other. I
                further understand that my application is being submitted to a lender(s).
              </p>
            </section>

            <section className="pdfcol-full">
              <div className="pdfcol-half">
                <h6>Applicant</h6>
                <p>
                  <input
                    className="mr-2"
                    name="checkbox2"
                    type="checkbox"
                    id="checkbox2"
                    checked={application?.agreeToTerms}
                  />
                  I agree to the Authorization
                </p>
                <h5>{application?.signature}</h5>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-form">
      <Row>
        <Col>
          <div className="application">
            <h2 className="pb-2 mt-4 mb-3 border-bottom">Commercial Application #{applicationId}</h2>
          </div>
        </Col>
      </Row>
      {isLoading ? <CircularProgressComponent /> : renderApplication()}
    </div>
  );
}
